import React from 'react';
import { FileType } from '@alucio/aws-beacon-amplify/src/models';
import {
  DNAButton,
  DNABox,
  DNACard,
  DNACheckbox,
  DNADivider,
  DNAText,
  Iffy,
  util,
} from '@alucio/lux-ui';
import {
  DNAChip,
  DNACardComponent,
  DNACustomFieldChipList,
  DNADocumentContextMenu,
  DNADocumentThumbnail,
  useDNADocumentActions,
  useCurrentPage,
} from 'src/components/DNA';
import { DocumentORM } from 'src/types/types';
import { useAppSettings } from 'src/state/context/AppSettings';
import { GRID_CARD_STYLES_VARIANTS_CONFIGS } from './constants';
import { styles } from './Styles';

export const DNADocumentDefaultCard: DNACardComponent<DocumentORM> = (props) => {
  const { ORM: documentORM, isOnline, onPress, selectEnabled, checked, onSelect } = props;
  const { deviceMode } = useAppSettings();
  const isDesktop = deviceMode === 'desktop';
  const documentActions = useDNADocumentActions();
  const isBookmarked = documentORM.meta.bookmark.isBookmarked;
  // [TODO-2126] - We should have a property that combines isOnline and isContentCached
  //               on the DocumentVersionORM rather than checking both at the component level
  const unavailableContent =
    !isOnline &&
    (!documentORM.relations.version.cachedDocumentVersionORM?.meta.assets.isContentCached ||
      documentORM.model.type === FileType.WEB);
  const canPresent = documentORM.meta.permissions.MSLPresent
  const safeOnPress = onPress
    ? () => { onPress?.(documentORM) }
    : undefined

  const thumbnailOnPress = canPresent
    ? documentActions.present(documentORM)
    : documentActions.preview(documentORM)
  const cardOnPress = documentActions.preview(documentORM)

  const route = useCurrentPage()
  const isPublisherRoute = route?.configOptions?.modules?.includes('publisher')
  const title = documentORM.relations.version.latestUsableDocumentVersionORM.model.title
  const onPressCard = unavailableContent
    ? undefined
    : (safeOnPress || cardOnPress)

  return (
    <DNACard
      testID="document-card-container"
      appearance="outline"
      interactive="pressable"
      onPress={onPressCard}
      style={util.mergeStyles(
        undefined,
        styles.cardContainerDesktop,
        [styles.cardContainerTablet, !isDesktop],
        [styles.unavailableContent, unavailableContent],
      )}
    >
      {/* ACTION BUTTONS */}
      <DNABox spacing="between">
        <DNABox spacing="xs" alignY="center">
          <Iffy is={selectEnabled}>
            <DNACheckbox
              context="altBg"
              style={styles.checkbox}
              checked={checked}
              onChange={onSelect}
            />
          </Iffy>
          <DNAButton
            appearance="ghost"
            status={isBookmarked ? 'primary' : 'subtle'}
            size={isDesktop ? 'md' : 'xl'}
            iconLeft={isBookmarked ? 'star-fill' : 'star' }
            onPress={documentActions.bookmark(documentORM)}
          />
        </DNABox>
        <DNADocumentContextMenu documentORM={documentORM}>
          <DNAButton
            appearance="ghost"
            status="subtle"
            size={isDesktop ? 'md' : 'xl'}
            iconLeft="dots-three-vertical-bold"
          />
        </DNADocumentContextMenu>
      </DNABox>
      {/* THUMBNAIL & TITLE & CHIPS */}
      <DNABox fill childFill={1} appearance="col" spacing="xs">
        {/* THUMBNAIL */}
        <DNADocumentThumbnail
          documentVersionORM={documentORM.relations.version.latestUsableDocumentVersionORM}
          width={GRID_CARD_STYLES_VARIANTS_CONFIGS.THUMBNAIL[deviceMode].width}
          height={GRID_CARD_STYLES_VARIANTS_CONFIGS.THUMBNAIL[deviceMode].height}
          unavailableContent={unavailableContent}
          /** Custom behavior here. Thumbs in Publisher routes just open the preview modal,
          * whereas MSL views will either present or preview based on whether a doc is internal */
          onPress={ unavailableContent ? undefined
            : isPublisherRoute ? safeOnPress || thumbnailOnPress : thumbnailOnPress
          }
          // [TODO] - Implement here - Nonhover
        />
        {/* TITLE */}
        <DNABox fill style={styles.cardTitleContainer}>
          <DNABox fill alignY="center">
            <DNAText testID="document-card-title" style={styles.cardTitle} numberOfLines={2}>
              {title}
            </DNAText>
          </DNABox>
        </DNABox>
        <DNADivider variant="secondary"/>
        {/* CHIPS */}
        <DNABox alignY="center" spacing="xs" style={styles.cardChipsContainer}>
          <DNACustomFieldChipList
            documentVersionORM={documentORM.relations.version.latestUsableDocumentVersionORM}
          />
          <Iffy is={unavailableContent}>
            <DNAChip.Beacon
              testID={`content-not-available-${title}`}
              variant="CONTENT_UNAVAILABLE"
            />
          </Iffy>
        </DNABox>
      </DNABox>
    </DNACard>
  );
}

export default DNADocumentDefaultCard
